"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  ThemeIcon,
  List,
} from "@mantine/core";
import {
  IconPalette,
  IconArrowRight,
  IconEye,
  IconSparkles,
  IconX,
  IconFlame,
  IconSnowflake,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { colorSeasons } from "@/constants";
import { useTranslations } from "next-intl";

export default function BrightSpringVsBrightWinterContent({ locale }: { locale: string }) {
  const t = useTranslations("brightSpringVsBrightWinter");
  const brightSpringData = colorSeasons.SPRING.subseasons.BRIGHT;
  const brightWinterData = colorSeasons.WINTER.subseasons.TRUE; // TRUE is Bright Winter

  const comparisonPoints = [
    {
      aspect: t("comparisonPoints.undertone.title"),
      brightSpring: t("comparisonPoints.undertone.spring"),
      brightWinter: t("comparisonPoints.undertone.winter"),
      icon: IconPalette,
    },
    {
      aspect: t("comparisonPoints.temperature.title"),
      brightSpring: t("comparisonPoints.temperature.spring"),
      brightWinter: t("comparisonPoints.temperature.winter"),
      icon: IconEye,
    },
    {
      aspect: t("comparisonPoints.metals.title"),
      brightSpring: t("comparisonPoints.metals.spring"),
      brightWinter: t("comparisonPoints.metals.winter"),
      icon: IconSparkles,
    },
    {
      aspect: t("comparisonPoints.keyDiff.title"),
      brightSpring: t("comparisonPoints.keyDiff.spring"),
      brightWinter: t("comparisonPoints.keyDiff.winter"),
      icon: IconX,
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            {t("hero.title")}
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            {t("hero.subtitle")}
          </Text>
        </Box>

        {/* Quick Visual Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            {t("coreDifference")}
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md" bg="orange.0">
              <Stack gap="md" ta="center">
                <ThemeIcon size={60} radius="xl" color="orange" variant="light">
                  <IconFlame size={30} />
                </ThemeIcon>
                <Title order={3}>{t("brightSpring.title")}</Title>
                <Text size="sm" c="dimmed">
                  {t("brightSpring.description")}
                </Text>
                <Badge color="orange" variant="light" size="lg">
                  {t("brightSpring.badge")}
                </Badge>
                <Group gap="xs" justify="center">
                  {brightSpringData.primaryColors.slice(0, 6).map((color, i) => (
                    <Box key={i} w={28} h={28} style={{ background: color.hex, borderRadius: 4 }} />
                  ))}
                </Group>
              </Stack>
            </Paper>
            <Paper withBorder p="lg" radius="md" bg="blue.0">
              <Stack gap="md" ta="center">
                <ThemeIcon size={60} radius="xl" color="blue" variant="light">
                  <IconSnowflake size={30} />
                </ThemeIcon>
                <Title order={3}>{t("brightWinter.title")}</Title>
                <Text size="sm" c="dimmed">
                  {t("brightWinter.description")}
                </Text>
                <Badge color="blue" variant="light" size="lg">
                  {t("brightWinter.badge")}
                </Badge>
                <Group gap="xs" justify="center">
                  {brightWinterData.primaryColors.slice(0, 6).map((color, i) => (
                    <Box key={i} w={28} h={28} style={{ background: color.hex, borderRadius: 4 }} />
                  ))}
                </Group>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Key Differences */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            {t("keyDifferences")}
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {comparisonPoints.map((point, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Stack gap="md">
                  <Group>
                    <ThemeIcon color="violet" variant="light" size="lg">
                      <point.icon size={20} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">
                      {point.aspect}
                    </Text>
                  </Group>
                  <Box>
                    <Text fw={500} c="orange" size="sm" mb="xs">
                      {t("brightSpring.title")}:
                    </Text>
                    <Text size="sm" mb="md">
                      {point.brightSpring}
                    </Text>
                    <Text fw={500} c="blue" size="sm" mb="xs">
                      {t("brightWinter.title")}:
                    </Text>
                    <Text size="sm">{point.brightWinter}</Text>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Color Palette Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            {t("paletteComparison")}
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} mb="md" ta="center" c="orange">{t("springColors")}</Title>
              <Text size="sm" c="dimmed" ta="center" mb="md">{t("springDesc")}</Text>
              <SimpleGrid cols={4} spacing="xs">
                {brightSpringData.primaryColors.slice(0, 8).map((color, index) => (
                  <Box key={index}>
                    <Box
                      style={{
                        backgroundColor: color.hex,
                        height: 40,
                        borderRadius: 4,
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <Text size="xs" ta="center" c="dimmed" mt={4}>{color.hex}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
            <Box>
              <Title order={3} mb="md" ta="center" c="blue">{t("winterColors")}</Title>
              <Text size="sm" c="dimmed" ta="center" mb="md">{t("winterDesc")}</Text>
              <SimpleGrid cols={4} spacing="xs">
                {brightWinterData.primaryColors.slice(0, 8).map((color, index) => (
                  <Box key={index}>
                    <Box
                      style={{
                        backgroundColor: color.hex,
                        height: 40,
                        borderRadius: 4,
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <Text size="xs" ta="center" c="dimmed" mt={4}>{color.hex}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </SimpleGrid>
        </Card>

        {/* How to Tell at Home */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            {t("howToTell")}
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h4" mb="md">{t("redTest.title")}</Title>
              <Text size="sm" c="dimmed" mb="sm">
                {t("redTest.description")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>{t("brightSpring.title")}:</strong> {t("redTest.spring")}</List.Item>
                <List.Item><strong>{t("brightWinter.title")}:</strong> {t("redTest.winter")}</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md">{t("metalTest.title")}</Title>
              <Text size="sm" c="dimmed" mb="sm">
                {t("metalTest.description")}
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>{t("brightSpring.title")}:</strong> {t("metalTest.spring")}</List.Item>
                <List.Item><strong>{t("brightWinter.title")}:</strong> {t("metalTest.winter")}</List.Item>
              </List>
            </Box>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="gray.0">
            <Text size="sm" c="dimmed">
              {t("homeTestNote")}
            </Text>
          </Paper>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              {t("ctaTitle")}
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              {t("ctaSubtitle")}
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                {t("confirmSeason")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                {t("startQuiz")}
              </Button>
            </Flex>
            <Group justify="center" gap="xl" mt="md">
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/spring/bright"
                size="sm"
                color="gray"
              >
                {t("viewSpringPalette")}
              </Button>
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/winter/true"
                size="sm"
                color="gray"
              >
                {t("viewWinterPalette")}
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
